import React, { useEffect, useState } from 'react';
import { Card } from '../ui/Card';
import { CalendarDays, Lock, CheckCircle2 } from 'lucide-react';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { googleCalendar } from '../../services/googleCalendar';
// Mock events
const EVENTS = [{
  id: 1,
  date: new Date().toISOString(),
  title: 'Sprint 24 Ends',
  type: 'jira',
  color: 'bg-blue-500'
}, {
  id: 2,
  date: new Date(Date.now() + 86400000 * 2).toISOString(),
  title: 'v1.2.0 Release',
  type: 'github',
  color: 'bg-purple-500'
}, {
  id: 3,
  date: new Date(Date.now() + 86400000 * 4).toISOString(),
  title: 'Sprint 25 Planning',
  type: 'jira',
  color: 'bg-blue-500'
}, {
  id: 4,
  date: new Date(Date.now() + 86400000 * 5).toISOString(),
  title: 'Review PRs',
  type: 'personal',
  color: 'bg-amber-500'
}];
export function CalendarWidget() {
  const {
    isConnected,
    signIn
  } = useGoogleAuth();
  const [realEvents, setRealEvents] = useState<any[]>([]);
  useEffect(() => {
    if (isConnected) {
      googleCalendar.getMonthEvents().then(events => {
        const mapped = events.map(e => ({
          id: e.id,
          date: e.start.dateTime || e.start.date || new Date().toISOString(),
          title: e.summary || '(Sin título)',
          type: 'google',
          color: 'bg-blue-500'
        }));
        setRealEvents(mapped);
      }).catch(console.error);
    } else {
      setRealEvents([]);
    }
  }, [isConnected]);
  const displayEvents = isConnected && realEvents.length > 0 ? realEvents : EVENTS;
  // Simple calendar grid generation for current month
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);
  const hasEvent = (day: number) => {
    return displayEvents.some(e => {
      const d = new Date(e.date);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
  };
  const getEventColors = (day: number) => {
    const dayEvents = displayEvents.filter(e => {
      const d = new Date(e.date);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
    return [...new Set(dayEvents.map(e => e.color))];
  };
  return <Card className="h-full bg-slate-900 border-slate-800 flex flex-col max-h-[400px]" data-id="element-3089">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between shrink-0" data-id="element-3090">
        <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2" data-id="element-3091">
          <CalendarDays size={16} className="text-indigo-400" data-id="element-3092" />
          Calendario
        </h3>
        <span className="text-xs text-slate-400 font-medium" data-id="element-3093">
          {today.toLocaleDateString('es-AR', {
          month: 'long',
          year: 'numeric'
        })}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 flex flex-col gap-6" data-id="element-3094">
        {/* Mini Calendar */}
        <div data-id="element-3095">
          <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-3096">
            {['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'].map(d => <div key={d} className="text-center text-[10px] font-semibold text-slate-500" data-id="element-3097">
                {d}
              </div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-id="element-3098">
            {days.map((day, i) => {
            const isToday = day === today.getDate();
            const colors = day ? getEventColors(day) : [];
            return <div key={i} className={`aspect-square flex flex-col items-center justify-center rounded-lg relative text-xs ${!day ? '' : isToday ? 'bg-indigo-600 text-white font-bold' : 'bg-slate-950 text-slate-300 hover:bg-slate-800'}`} data-id="element-3099">
                  {day}
                  {colors.length > 0 && <div className="absolute bottom-1 flex gap-0.5" data-id="element-3100">
                      {colors.map((c, j) => <span key={j} className={`w-1 h-1 rounded-full ${c}`} data-id="element-3101"></span>)}
                    </div>}
                </div>;
          })}
          </div>
        </div>

        {/* Upcoming Events */}
        <div data-id="element-3102">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-3103">
            Próximos Eventos
          </h4>
          <div className="space-y-2" data-id="element-3104">
            {displayEvents.slice(0, 4).map(event => {
            const d = new Date(event.date);
            return <div key={event.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-950 border border-slate-800" data-id="element-3105">
                  <div className={`w-1.5 h-1.5 rounded-full ${event.color} shrink-0`} data-id="element-3106"></div>
                  <div className="flex-1 min-w-0" data-id="element-3107">
                    <p className="text-xs font-medium text-slate-200 truncate" data-id="element-3108">
                      {event.title}
                    </p>
                  </div>
                  <span className="text-[10px] text-slate-500 shrink-0" data-id="element-3109">
                    {d.toLocaleDateString('es-AR', {
                  day: '2-digit',
                  month: 'short'
                })}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Integrations Status */}
        <div data-id="element-3110">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-3111">
            Integraciones
          </h4>
          <div className="space-y-2" data-id="element-3112">
            <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-950/20 border border-emerald-900/30" data-id="element-3113">
              <span className="text-xs text-slate-300 flex items-center gap-2" data-id="element-3114">
                <CheckCircle2 size={12} className="text-emerald-500" data-id="element-3115" /> Jira
              </span>
              <span className="text-[10px] text-emerald-500" data-id="element-3116">Conectado</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-950/20 border border-emerald-900/30" data-id="element-3117">
              <span className="text-xs text-slate-300 flex items-center gap-2" data-id="element-3118">
                <CheckCircle2 size={12} className="text-emerald-500" data-id="element-3119" /> GitHub
              </span>
              <span className="text-[10px] text-emerald-500" data-id="element-3120">Conectado</span>
            </div>

            {isConnected ? <div className="flex items-center justify-between p-2 rounded-lg bg-emerald-950/20 border border-emerald-900/30" data-id="element-3121">
                <span className="text-xs text-slate-300 flex items-center gap-2" data-id="element-3122">
                  <CheckCircle2 size={12} className="text-emerald-500" data-id="element-3123" /> Google
                  Workspace
                </span>
                <span className="text-[10px] text-emerald-500" data-id="element-3124">Conectado</span>
              </div> : <div className="p-2 rounded-lg bg-slate-950 border border-slate-800" data-id="element-3125">
                <div className="flex items-center justify-between mb-2" data-id="element-3126">
                  <span className="text-xs text-slate-400 flex items-center gap-2" data-id="element-3127">
                    <Lock size={12} data-id="element-3128" /> Google Workspace
                  </span>
                </div>
                <button onClick={() => signIn()} className="w-full py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold rounded transition-colors" data-id="element-3129">
                  Conectar Google
                </button>
              </div>}

            {/* Coming Soon Integrations */}
            {[{
            name: 'Microsoft Outlook',
            scopes: 'Calendars.Read',
            endpoints: 'GET /api/microsoft/calendar/events'
          }].map(int => <div key={int.name} className="p-2 rounded-lg bg-slate-950 border border-slate-800 opacity-60" data-id="element-3130">
                <div className="flex items-center justify-between mb-1" data-id="element-3131">
                  <span className="text-xs text-slate-400 flex items-center gap-2" data-id="element-3132">
                    <Lock size={12} data-id="element-3133" /> {int.name}
                  </span>
                  <span className="text-[9px] px-1.5 py-0.5 bg-slate-800 text-slate-500 rounded uppercase" data-id="element-3134">
                    Próximamente
                  </span>
                </div>
                <div className="pl-5" data-id="element-3135">
                  <p className="text-[9px] text-slate-600 font-mono" data-id="element-3136">
                    Req: OAuth + {int.endpoints}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </Card>;
}